"use client";

import { Send } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

export type PracticeRecordingHandle = {
  getEnglishAudioBlob: () => Promise<Blob | null>;
  getTeluguAudioBlob: () => Promise<Blob | null>;
  getEnglishAudioURL: () => string | null;
  getTeluguAudioURL: () => string | null;
  hasEnglishRecording: () => boolean;
  hasTeluguRecording: () => boolean;
};

type Props = {
  englishWord: string;
  teluguWord: string;
  onSubmit?: () => void;
  isSubmitting?: boolean;
};

// ── Static waveform + play/pause shown AFTER recording ───────────────────────
const WaveformPlayer = ({
  isPlaying,
  onToggle,
}: {
  isPlaying: boolean;
  onToggle: () => void;
}) => {
  const heights = [3, 5, 8, 6, 10, 7, 4, 9, 6, 8, 5, 11, 7, 4, 9, 6, 10, 5, 8, 6, 4, 9, 7, 5, 8, 6, 4, 3];
  return (
    <div className="flex items-center gap-2 rounded-2xl px-3 py-2 w-full bg-purple-100">
      <button
        onClick={onToggle}
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow bg-gradient-to-br from-purple-500 to-pink-500"
      >
        {isPlaying ? (
          <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16" rx="1" />
            <rect x="14" y="4" width="4" height="16" rx="1" />
          </svg>
        ) : (
          <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>
      <div className="flex items-center gap-[2px] flex-1 h-8">
        {heights.map((h, i) => (
          <div
            key={i}
            className="rounded-full flex-1"
            style={{
              height: `${h * 2.2}px`,
              background: isPlaying ? "#a855f7" : "#c084fc",
              transition: "height 0.15s ease",
            }}
          />
        ))}
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const PracticeRecording = React.forwardRef<PracticeRecordingHandle, Props>(
  function PracticeRecordingComponent(
    { englishWord, teluguWord, onSubmit, isSubmitting = false },
    ref
  ) {
    const [isRecordingEnglish, setIsRecordingEnglish] = useState(false);
    const [englishAudioURL, setEnglishAudioURL] = useState<string | null>(null);
    const [isPlayingEnglish, setIsPlayingEnglish] = useState(false);
    const englishMediaRecorderRef = useRef<MediaRecorder | null>(null);
    const englishAudioChunksRef = useRef<Blob[]>([]);
    const englishAudioRef = useRef<HTMLAudioElement | null>(null);

    const [isRecordingTelugu, setIsRecordingTelugu] = useState(false);
    const [teluguAudioURL, setTeluguAudioURL] = useState<string | null>(null);
    const [isPlayingTelugu, setIsPlayingTelugu] = useState(false);
    const teluguMediaRecorderRef = useRef<MediaRecorder | null>(null);
    const teluguAudioChunksRef = useRef<Blob[]>([]);
    const teluguAudioRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
      if (englishAudioRef.current) {
        englishAudioRef.current.onended = () => setIsPlayingEnglish(false);
      }
    }, [englishAudioURL]);

    useEffect(() => {
      if (teluguAudioRef.current) {
        teluguAudioRef.current.onended = () => setIsPlayingTelugu(false);
      }
    }, [teluguAudioURL]);

    React.useImperativeHandle(ref, () => ({
      getEnglishAudioBlob: async () => {
        if (englishAudioURL) {
          const response = await fetch(englishAudioURL);
          return await response.blob();
        }
        return null;
      },
      getTeluguAudioBlob: async () => {
        if (teluguAudioURL) {
          const response = await fetch(teluguAudioURL);
          return await response.blob();
        }
        return null;
      },
      getEnglishAudioURL: () => englishAudioURL,
      getTeluguAudioURL: () => teluguAudioURL,
      hasEnglishRecording: () => !!englishAudioURL,
      hasTeluguRecording: () => !!teluguAudioURL,
    }));

    const startEnglishRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        englishMediaRecorderRef.current = new MediaRecorder(stream);
        englishAudioChunksRef.current = [];
        englishMediaRecorderRef.current.ondataavailable = (event) => {
          englishAudioChunksRef.current.push(event.data);
        };
        englishMediaRecorderRef.current.onstop = () => {
          const audioBlob = new Blob(englishAudioChunksRef.current, { type: "audio/wav" });
          setEnglishAudioURL(URL.createObjectURL(audioBlob));
          stream.getTracks().forEach((track) => track.stop());
        };
        englishMediaRecorderRef.current.start();
        setIsRecordingEnglish(true);
      } catch (error) {
        console.error("Error accessing microphone:", error);
        alert("Please allow microphone access to record your voice!");
      }
    };

    const stopEnglishRecording = () => {
      if (englishMediaRecorderRef.current && isRecordingEnglish) {
        englishMediaRecorderRef.current.stop();
        setIsRecordingEnglish(false);
      }
    };

    const playEnglishRecording = () => {
      if (englishAudioURL && englishAudioRef.current) {
        if (isPlayingEnglish) {
          englishAudioRef.current.pause();
          setIsPlayingEnglish(false);
        } else {
          englishAudioRef.current.play();
          setIsPlayingEnglish(true);
        }
      }
    };

    const startTeluguRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        teluguMediaRecorderRef.current = new MediaRecorder(stream);
        teluguAudioChunksRef.current = [];
        teluguMediaRecorderRef.current.ondataavailable = (event) => {
          teluguAudioChunksRef.current.push(event.data);
        };
        teluguMediaRecorderRef.current.onstop = () => {
          const audioBlob = new Blob(teluguAudioChunksRef.current, { type: "audio/wav" });
          setTeluguAudioURL(URL.createObjectURL(audioBlob));
          stream.getTracks().forEach((track) => track.stop());
        };
        teluguMediaRecorderRef.current.start();
        setIsRecordingTelugu(true);
      } catch (error) {
        console.error("Error accessing microphone:", error);
        alert("Please allow microphone access to record your voice!");
      }
    };

    const stopTeluguRecording = () => {
      if (teluguMediaRecorderRef.current && isRecordingTelugu) {
        teluguMediaRecorderRef.current.stop();
        setIsRecordingTelugu(false);
      }
    };

    const playTeluguRecording = () => {
      if (teluguAudioURL && teluguAudioRef.current) {
        if (isPlayingTelugu) {
          teluguAudioRef.current.pause();
          setIsPlayingTelugu(false);
        } else {
          teluguAudioRef.current.play();
          setIsPlayingTelugu(true);
        }
      }
    };

    const canSubmit =
      (!!englishAudioURL && !isRecordingEnglish) ||
      (!!teluguAudioURL && !isRecordingTelugu);

    // Mutual exclusivity conditions
    const isEnglishDisabled = isSubmitting || isRecordingTelugu || !!teluguAudioURL;
    const isTeluguDisabled = isSubmitting || isRecordingEnglish || !!englishAudioURL;

    return (
      <div className="flex flex-col items-center gap-3 w-full">
        <div className="grid grid-cols-2 gap-4 w-full">

          {/* ── English Column ── */}
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={isRecordingEnglish ? stopEnglishRecording : startEnglishRecording}
              disabled={isEnglishDisabled}
              className={`w-16 h-16 rounded-full shadow-2xl transform hover:scale-110 transition-all duration-300 flex items-center justify-center relative
                ${isRecordingEnglish
                  ? "bg-gradient-to-br from-pink-400 to-rose-500"
                  : "bg-gradient-to-br from-purple-500 to-pink-500"
                }
                ${isEnglishDisabled ? "opacity-50 cursor-not-allowed transform-none hover:scale-100" : ""}
              `}
            >
              {isRecordingEnglish && (
                <>
                  <span className="absolute inset-0 rounded-full border-4 border-pink-400 animate-ping opacity-60" />
                  <span className="absolute inset-[-6px] rounded-full border-2 border-pink-300 animate-pulse opacity-40" />
                </>
              )}
              <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1a4 4 0 0 1 4 4v6a4 4 0 0 1-8 0V5a4 4 0 0 1 4-4zm0 2a2 2 0 0 0-2 2v6a2 2 0 0 0 4 0V5a2 2 0 0 0-2-2zm6 8a1 1 0 0 1 1 1 7 7 0 0 1-6 6.92V21h2a1 1 0 0 1 0 2H9a1 1 0 0 1 0-2h2v-2.08A7 7 0 0 1 5 12a1 1 0 0 1 2 0 5 5 0 0 0 10 0 1 1 0 0 1 1-1z" />
              </svg>
            </button>

            <div className="text-center">
              <p className="text-sm font-semibold text-purple-800">{englishWord}</p>
              <p className="text-xs text-purple-400">(English)</p>
            </div>

            {isRecordingEnglish && (
              <p className="text-center text-sm font-bold text-pink-500">
                Recording... tap to stop
              </p>
            )}

            {englishAudioURL && !isRecordingEnglish && (
              <div className="w-full">
                <WaveformPlayer
                  isPlaying={isPlayingEnglish}
                  onToggle={playEnglishRecording}
                />
              </div>
            )}
          </div>

          {/* ── Telugu Column ── */}
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={isRecordingTelugu ? stopTeluguRecording : startTeluguRecording}
              disabled={isTeluguDisabled}
              className={`w-16 h-16 rounded-full shadow-2xl transform hover:scale-110 transition-all duration-300 flex items-center justify-center relative
                ${isRecordingTelugu
                  ? "bg-gradient-to-br from-pink-400 to-rose-500"
                  : "bg-gradient-to-br from-purple-500 to-pink-500"
                }
                ${isTeluguDisabled ? "opacity-50 cursor-not-allowed transform-none hover:scale-100" : ""}
              `}
            >
              {isRecordingTelugu && (
                <>
                  <span className="absolute inset-0 rounded-full border-4 border-pink-400 animate-ping opacity-60" />
                  <span className="absolute inset-[-6px] rounded-full border-2 border-pink-300 animate-pulse opacity-40" />
                </>
              )}
              <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1a4 4 0 0 1 4 4v6a4 4 0 0 1-8 0V5a4 4 0 0 1 4-4zm0 2a2 2 0 0 0-2 2v6a2 2 0 0 0 4 0V5a2 2 0 0 0-2-2zm6 8a1 1 0 0 1 1 1 7 7 0 0 1-6 6.92V21h2a1 1 0 0 1 0 2H9a1 1 0 0 1 0-2h2v-2.08A7 7 0 0 1 5 12a1 1 0 0 1 2 0 5 5 0 0 0 10 0 1 1 0 0 1 1-1z" />
              </svg>
            </button>

            <div className="text-center">
              <p className="text-sm font-semibold text-purple-800">{teluguWord}</p>
              <p className="text-xs text-purple-400">(Telugu)</p>
            </div>

            {isRecordingTelugu && (
              <p className="text-center text-sm font-bold text-pink-500">
                Recording... tap to stop
              </p>
            )}

            {teluguAudioURL && !isRecordingTelugu && (
              <div className="w-full">
                <WaveformPlayer
                  isPlaying={isPlayingTelugu}
                  onToggle={playTeluguRecording}
                />
              </div>
            )}
          </div>

        </div>

        {/* Submit */}
        {canSubmit && (
          <div className="w-full space-y-2">
            <button
              onClick={onSubmit}
              disabled={isSubmitting}
              className={`w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-3 px-4 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 ${
                isSubmitting ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit
                </>
              )}
            </button>
          </div>
        )}

        {englishAudioURL && (
          <audio ref={englishAudioRef} src={englishAudioURL} style={{ display: "none" }} />
        )}
        {teluguAudioURL && (
          <audio ref={teluguAudioRef} src={teluguAudioURL} style={{ display: "none" }} />
        )}
      </div>
    );
  }
);

export default PracticeRecording;
